import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { PageMode } from "@/pages/tina/festivals/lib/page-request";
import type { FestivalPageRead } from "@/types/tina-festival-page";

interface ActionsMenuProps {
  page: FestivalPageRead;
  mode: PageMode;
  busy: boolean;
  onKind: () => void;
  onMerge: () => void;
  onSplit: () => void;
  onOwnPage: () => void;
  onUnpublish: () => void;
}

/** The header's "Actions": what a curator can do to the festival besides its main button, by where it stands. */
export function ActionsMenu({
  page,
  mode,
  busy,
  onKind,
  onMerge,
  onSplit,
  onOwnPage,
  onUnpublish,
}: ActionsMenuProps) {
  const proposed = mode === "approve" || mode === "group";
  // A festival taken out of Mood approves again before its type can change (Tina answers `unpublished_record`).
  const canChangeKind = !(mode === "changes" && page.unpublished);
  const canUnpublish =
    (mode === "changes" && !page.unpublished) ||
    (mode === "program_live" && page.backend_group_id !== null);
  if (!proposed && mode !== "changes" && mode !== "program_live") return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" disabled={busy}>
          Actions
          <ChevronDown className="ml-1 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canChangeKind ? (
          <DropdownMenuItem onSelect={onKind}>Change type…</DropdownMenuItem>
        ) : null}
        {proposed ? (
          <>
            <DropdownMenuItem onSelect={onMerge}>
              Merge into another festival…
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onSplit}>
              Split pages into a new festival…
            </DropdownMenuItem>
          </>
        ) : null}
        {mode === "approve" || mode === "changes" ? (
          <DropdownMenuItem onSelect={onOwnPage}>
            Mark the festival&apos;s own page…
          </DropdownMenuItem>
        ) : null}
        {canUnpublish ? (
          <DropdownMenuItem
            disabled={page.status === "publishing"}
            onSelect={onUnpublish}
          >
            Unpublish…
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
